/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'sharedUi/Dialog';
import { evalCasesService } from '../../services/eval-cases.service';
import EvalCaseDetails from '../../pages/eval-cases/eval-case-details';
import { EmptyState } from '../common/panel';

/**
 * Opens one eval case's details in a dialog, reusing the same EvalCaseDetails
 * body as the full-page case view. Used from the Results tab of a run so a case
 * can be inspected without leaving the run.
 *
 * The identity lives in the dialog header rather than the body, so the name is
 * still on screen once the body scrolls, and the body is asked for its embedded
 * shape so the case does not arrive as a card floating inside a card.
 */

/** Placeholder in the body's own shape: a chip row, then the Input/Expected pair.
 *  A spinner says only "wait"; this says what is about to appear and holds the
 *  height, so the dialog does not jump when the case lands. */
const Loading = () => (
  <div className="flex flex-col gap-5" aria-busy="true" aria-label="Loading eval case">
    <div className="flex gap-1.5">
      <span className="ui-skel h-[18px] w-16" />
      <span className="ui-skel h-[18px] w-24" />
    </div>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {['Input', 'Expected result'].map((label) => (
        <div key={label}>
          <span className="ui-skel h-2.5 w-20" />
          <span className="ui-skel mt-2 h-28 !rounded-lg" />
        </div>
      ))}
    </div>
  </div>
);

const EvalCaseViewDialog = ({ caseRef, visible, onHide }: {
  caseRef?: string | null;
  visible: boolean;
  onHide: () => void;
}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['eval-case', caseRef],
    queryFn: () => evalCasesService.getById(String(caseRef)),
    enabled: !!caseRef && visible,
  });

  const header = data ? (
    <div className="flex min-w-0 items-center gap-2.5">
      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
        <i className="pi pi-list-check" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[15px] font-semibold tracking-[-0.01em] text-ink">{data.name}</span>
        {data.caseCode && (
          <span className="block truncate font-mono text-[11px] font-normal text-ink-muted">{data.caseCode}</span>
        )}
      </span>
    </div>
  ) : 'Eval case';

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      style={{ width: '720px', maxWidth: '95vw' }}
      dismissableMask
      className="eval-case-dialog"
      data-testid="EvalCaseViewDialog"
    >
      {isLoading ? <Loading />
        : isError || !data ? (
          <EmptyState
            icon="pi pi-exclamation-triangle"
            title="This case could not be loaded"
            hint={(error as any)?.message || 'The case may have been deleted since the run recorded it.'}
          />
        ) : <EvalCaseDetails data={data} embedded />}
    </Dialog>
  );
};

export default EvalCaseViewDialog;
